import { PixelCanvas } from "#canvas";
import { type Layer, type PixelCanvasRef } from "#canvas/types.ts";
import { type BrushSize, BrushSizeSchema, COLS, ROWS } from "#puf/constants.ts";
import { emptyFrameLayers } from "#puf/helpers.ts";
import { store } from "#puf/store/index.ts";
import { useSelector } from "@xstate/store-react";
import { type Ref, useCallback } from "react";

// Schema does the clamping — out-of-range candidates fall back to current.
function bumpBrushSize(current: BrushSize, direction: 1 | -1): BrushSize {
  return BrushSizeSchema.safeParse(current + direction).data ?? current;
}

interface Props {
  ref: Ref<PixelCanvasRef> | undefined;
  onCommit: (layers: Layer[]) => void;
  onPick: (color: string) => void;
}

export function CanvasArea({ ref, onCommit, onPick }: Props) {
  const color = useSelector(store, (s) => s.context.color);
  const mode = useSelector(store, (s) => s.context.mode);
  const brushSize = useSelector(store, (s) => s.context.brushSize);
  const shapeFill = useSelector(store, (s) => s.context.shapeFill);
  const frames = useSelector(store, (s) => s.context.frames);
  const activeFrame = useSelector(store, (s) => s.context.activeFrame);
  const activeLayerId = useSelector(store, (s) => s.context.activeLayerId);

  const layers = frames[activeFrame] ?? emptyFrameLayers();

  const onBrushBump = useCallback((direction: 1 | -1) => {
    store.trigger["brushSize.set"]({ size: bumpBrushSize(brushSize, direction) });
  }, [brushSize]);

  return (
    <main className="@container flex items-center justify-center bg-bg p-3 lg:p-0 lg:col-start-2 lg:row-start-1 lg:min-h-0 lg:min-w-0">
      <div className="w-full aspect-square max-w-[min(70dvh,640px)] mx-auto bg-bg-3 border border-line rounded-sm overflow-hidden lg:w-[min(90cqw,90cqh,720px)] lg:h-auto lg:max-w-none lg:max-h-full lg:aspect-square lg:mx-0">
        <PixelCanvas
          ref={ref ?? null}
          rows={ROWS}
          cols={COLS}
          layers={layers}
          activeLayerId={activeLayerId}
          color={color}
          mode={mode}
          brushSize={brushSize}
          shapeFill={shapeFill}
          onCommit={onCommit}
          onPick={onPick}
          onBrushBump={onBrushBump}
          bg="#1f2125"
          gridStroke="#2a2d31"
        />
      </div>
    </main>
  );
}
